import {
    Component,
    ComponentInterface,
    Element,
    h,
    Host,
    Listen,
    Prop,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import serializeForm from "../../utils/serializeForm";
import api from "../../utils/api";
import waitForHydrated from "../../utils/waitForHydrated";
import { track } from "../../utils/tracking";

const baseClass = "ux-iws-search-results";

@Component({
    tag: "ux-iws-search-results",
    styleUrl: "search-results.scss",
    shadow: false,
})
export class SearchResults implements ComponentInterface {
    /** The container DOM element */
    @Element() domNode: HTMLUxIwsSearchResultsElement;

    /** The search result category */
    @Prop() category: string = "";

    /** API endpoint from which html content for current filters and sorting is requested */
    @Prop() url: string = "";

    @Listen("uxFilterChange")
    onUxFilterChange(e: CustomEvent) {
        if (this.filterForm === null) {
            this.connectFilterForm();
        }
        this.formHandler(e);
    }

    /** Storage for the filter form domNode */
    private filterForm: HTMLFormElement | null = null;

    /** Storage for the sort form domNode */
    private sortForm: HTMLFormElement | null = null;

    /** Send the API request and handle response if url has not changed*/
    private requestData = () => {
        const { url } = this;
        if (url === "") return;
        const query = [
            serializeForm(this.filterForm),
            serializeForm(this.sortForm),
        ].join("&");
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: query,
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };
        api<string>(url, options)
            .then((html) => {
                this.replaceResults(html);
            })
            .then(() => this.trackLinks())
            .catch((err) => {
                console.log(err);
            });
    };

    private requestDataDebounced = debounce(this.requestData, 20);

    private getResultsContainer = (): HTMLDivElement => {
        return this.domNode.querySelector(
            `.${baseClass}__results-container`
        ) as HTMLDivElement;
    };

    private requestInitialData = () => {
        const resultsContainer = this.getResultsContainer();
        if (resultsContainer === null) {
            return;
        }
        const containerItems = [...resultsContainer.childNodes].filter((node) =>
            node.nodeName.toLowerCase().startsWith("ux-iws")
        );
        if (containerItems.length === 0) {
            this.requestDataDebounced();
        }
    };

    private formHandler = (e: Event) => {
        if (e && e.type === "submit") {
            e.preventDefault();
        }
        this.requestDataDebounced();
    };

    private connectFilter() {
        window.requestAnimationFrame(() => {
            const filter =
                this.domNode.querySelector<HTMLElement>(".ux-filter");
            if (filter !== null) {
                waitForHydrated(filter, () => {
                    this.connectFilterForm();
                });
            }
        });
    }

    private connectFilterForm = () => {
        this.disconnectForm(this.filterForm);
        this.filterForm = this.domNode.querySelector(
            ".ux-filter form"
        ) as HTMLFormElement;
        this.connectForm(this.filterForm);
    };

    private connectSortForm = () => {
        this.disconnectForm(this.sortForm);
        this.sortForm = this.domNode.querySelector(
            ".ux-iws-search-results__sort-container form"
        ) as HTMLFormElement;
        this.connectForm(this.sortForm);
    };

    private connectForm = (form: HTMLFormElement | null) => {
        if (form !== null) {
            form.addEventListener("change", this.formHandler);
            form.addEventListener("submit", this.formHandler);
        }
    };

    private disconnectForm = (form: HTMLFormElement | null) => {
        if (form !== null) {
            form.removeEventListener("change", this.formHandler);
            form.removeEventListener("submit", this.formHandler);
        }
    };

    private replaceResults = (html: string | undefined) => {
        if (typeof html !== "string" || html.length === 0) {
            return;
        }
        const resultsContainer = this.getResultsContainer();
        if (resultsContainer === null) {
            return;
        }
        resultsContainer.style.height = `${resultsContainer.offsetHeight}px`;
        resultsContainer.innerHTML = html;
        const resultSummaryEl = resultsContainer.querySelector<HTMLElement>(
            `[rel="results-summary"]`
        );
        if (resultSummaryEl) {
            const headerResultSummaryEl =
                this.domNode.querySelector<HTMLElement>(
                    ".ux-iws-search-results__summary"
                );
            if (headerResultSummaryEl) {
                headerResultSummaryEl.innerHTML = resultSummaryEl.innerHTML;
            }
            resultSummaryEl.remove();
        }
        setTimeout(() => {
            resultsContainer.style.height = "auto";
        }, 200);
    };

    private onClick = (e: Event) => {
        const link = e.currentTarget as HTMLAnchorElement;
        const tabTarget = link.getAttribute("data-ux-tab-target");
        const isTile = link.classList.contains("ux-iws-view-all-block");
        track("click_search_show_all", {
            category: tabTarget,
            type: isTile ? "tile" : "link",
        });
    };

    private trackLinks = () => {
        const showAllLinks = this.domNode.querySelectorAll(`
            .ux-iws-search-results__summary a[data-ux-tab-target],
            .ux-iws-view-all-block[data-ux-tab-target]
            `);
        showAllLinks.forEach((link) => {
            link.removeEventListener("click", this.onClick);
            link.addEventListener("click", this.onClick);
        });
    };

    componentDidLoad() {
        this.trackLinks();
        this.requestInitialData();
    }

    connectedCallback() {
        this.connectFilter();
        this.connectSortForm();
    }

    disconnectedCallback() {
        this.disconnectForm(this.filterForm);
        this.disconnectForm(this.sortForm);
    }

    render() {
        return (
            <Host class={baseClass}>
                <div class={`${baseClass}__inner`}>
                    <slot />
                </div>
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
